/**
 * One Answered-so-far row — #344 icon actions + inline editor (no scroll to guided card).
 */
import { useEffect, useState } from 'react'
import { Pencil } from 'lucide-react'
import { formatMoney, parseMoneyInput } from '../../../../lib/formatters'
import MoneyInput from '../../../../components/MoneyInput'
import MoneyWordsConfirm, { saveEnabledForMoney } from './MoneyWordsConfirm'
import type { QuestionSavePayload } from './types'
import type { GuidedQuestion } from '../../../../lib/costsMarginQuestions'
import { localDateKey } from '../../../../lib/localDate'

export type InlinePrefill = { amount: string; text?: string }

export default function AnsweredRow({
  question,
  label,
  subline,
  answered,
  value,
  editable,
  prefill,
  busy,
  subOptions = [],
  onSave,
}: {
  question: GuidedQuestion
  label: string
  subline?: string
  answered: boolean
  value?: string
  editable: boolean
  prefill?: InlinePrefill
  busy: boolean
  subOptions?: Array<{ id: number; label: string }>
  onSave: (payload: QuestionSavePayload) => Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const [amount, setAmount] = useState('')
  const [desc, setDesc] = useState('')
  const [pv, setPv] = useState<number | ''>('')
  const [wordsOk, setWordsOk] = useState(false)
  const [due, setDue] = useState(localDateKey())

  useEffect(() => {
    if (!open) return
    setAmount(prefill?.amount ?? '')
    setDesc(prefill?.text ?? '')
    setPv('')
    setWordsOk(false)
    setDue(localDateKey())
  }, [open, prefill?.amount, prefill?.text])

  const dollars = parseMoneyInput(amount)
  const moneyKind = question.kind === 'subcontract_amount' || question.kind === 'labor' || question.kind === 'overhead'
  const canSave = moneyKind
    ? saveEnabledForMoney(dollars, wordsOk)
    : Number.isFinite(dollars) && dollars >= 0 && saveEnabledForMoney(dollars, wordsOk)
      && (question.kind !== 'sub_change_orders' || pv !== '')
      && (question.kind !== 'other_costs' || desc.trim().length > 0 || dollars > 0)

  async function save() {
    if (!canSave) return
    const cents = Math.round(dollars * 100)
    if (question.kind === 'subcontract_amount' && question.projectVendorId != null) {
      await onSave({ kind: 'subcontract_amount', projectVendorId: question.projectVendorId, amount_cents: cents })
    } else if (question.kind === 'labor') {
      await onSave({
        kind: 'job_cost',
        description: prefill?.text || 'Own labor',
        category: 'labor',
        amount_cents: cents,
        due_date: due || localDateKey(),
      })
    } else if (question.kind === 'overhead') {
      await onSave({
        kind: 'job_cost',
        description: prefill?.text || 'Overhead / general conditions',
        category: 'other',
        amount_cents: cents,
        due_date: due || localDateKey(),
      })
    } else if (question.kind === 'other_costs') {
      await onSave({
        kind: 'job_cost',
        description: desc.trim() || 'Job cost',
        category: 'other',
        amount_cents: cents,
        due_date: due || localDateKey(),
      })
    } else if (question.kind === 'sub_change_orders' && pv !== '') {
      const vendorName = subOptions.find((s) => s.id === pv)?.label
      await onSave({
        kind: 'sub_change_order',
        description: desc.trim() || 'Sub change order',
        amount_dollars: dollars,
        project_vendor_id: pv,
        vendor_name: vendorName,
      })
    } else return
    setOpen(false)
  }

  return (
    <div className="cm-answer-row" data-testid={`cm-answer-${question.id}`} data-inline-open={open ? 'true' : undefined}>
      <span className="cm-answer-label">
        {label}
        {subline && answered ? <span className="cm-answer-sub">{subline}</span> : null}
      </span>
      {open ? (
        <div className="cm-inline-edit" data-testid={`cm-inline-edit-${question.id}`}>
          {(question.kind === 'other_costs' || question.kind === 'sub_change_orders') && (
            <input
              className="cm-inline-desc"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Description"
              aria-label="Description"
              data-testid={`cm-inline-desc-${question.id}`}
            />
          )}
          {question.kind === 'sub_change_orders' && (
            <select
              value={pv === '' ? '' : String(pv)}
              onChange={(e) => setPv(e.target.value === '' ? '' : Number(e.target.value))}
              aria-label="Sub"
              data-testid={`cm-inline-sub-${question.id}`}
            >
              <option value="">Select sub…</option>
              {subOptions.map((s) => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          )}
          {(question.kind === 'labor' || question.kind === 'overhead' || question.kind === 'other_costs') && (
            <label className="cm-inline-due">
              When is this paid?
              <input
                type="date"
                value={due}
                onChange={(e) => setDue(e.target.value)}
                required
                aria-label="When is this paid?"
                data-testid={`cm-inline-due-${question.id}`}
              />
            </label>
          )}
          <MoneyInput
            className="cm-inline-amount money"
            value={amount}
            onChange={(v) => { setAmount(v); setWordsOk(false) }}
            aria-label="Amount"
            data-testid={`cm-inline-input-${question.id}`}
          />
          <MoneyWordsConfirm dollars={dollars} confirmed={wordsOk} onConfirmedChange={setWordsOk} />
          <div className="cm-inline-actions">
            <button type="button" className="cm-primary" disabled={busy || !canSave} onClick={() => void save()} data-testid={`cm-inline-save-${question.id}`}>
              Save
            </button>
            <button type="button" className="cm-btn" disabled={busy} onClick={() => setOpen(false)} data-testid={`cm-inline-cancel-${question.id}`}>
              Cancel
            </button>
          </div>
        </div>
      ) : answered ? (
        <strong className="money">{value}</strong>
      ) : (
        <span className="cm-not-answered" data-testid={`cm-not-answered-${question.id}`}>not answered</span>
      )}
      {editable && !open ? (
        <span className="hub-row-actions" data-testid={`cm-answer-actions-${question.id}`}>
          <button
            type="button"
            className="hub-row-action-btn"
            aria-label={answered ? `Edit ${label}` : `Answer ${label}`}
            onClick={() => setOpen(true)}
            data-testid={`cm-answer-action-${question.id}`}
          >
            <Pencil size={16} strokeWidth={2} aria-hidden />
          </button>
        </span>
      ) : (
        <span className="hub-row-actions cm-answer-actions-spacer" aria-hidden />
      )}
    </div>
  )
}

export function fmtCents(cents: number) {
  return formatMoney(cents / 100)
}
